import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Briefcase,
  Check,
  ChevronRight,
  Eye,
  Globe,
  Link as LinkIcon,
  MapPin,
  Pencil,
  Plus,
  ShieldCheck,
  Trophy,
} from 'lucide-react';
import { buildCandidateProfilePreviewUrl } from '../../utils/recruiterProfileUrl';
import OnboardingBoostModal, { type BoostModalKind } from './OnboardingBoostModal';
import ExperienceEditModal from './ExperienceEditModal';
import ProfileEditModal from './ProfileEditModal';
import SkillsEditModal from './SkillsEditModal';
import ProjectsEditModal from './ProjectsEditModal';
import OnboardingPromptSection from './OnboardingPromptSection';
import { profileStrengthLabel, resolveOnboardingProfileStrength } from './onboardingScoring';
import type { OnboardingDraft, ScreeningPromptItem } from './types';
import type { CreatePromptDocumentUploadState } from '../Create-Prompt/useCreatePromptDocumentUploads';
import type { CreatePromptVideoUploadState } from '../Create-Prompt/useCreatePromptVideoUploads';
import './FastOnboardingReview.css';

type ReviewStageProps = {
  draft: OnboardingDraft;
  targetRole: string;
  linkedin: string;
  resumeUrl?: string;
  sessionUser: Record<string, unknown> | null;
  saving: boolean;
  promptItems: ScreeningPromptItem[];
  videoFiles: Record<number, File>;
  documentFiles: Record<number, File>;
  videoUrls: Record<number, string>;
  documentUrls: Record<number, string>;
  videoUploads: Record<number, CreatePromptVideoUploadState>;
  documentUploads: Record<number, CreatePromptDocumentUploadState>;
  initials: string;
  onPersistDraft: (
    changes: { draftPatch?: Partial<OnboardingDraft>; targetRole?: string },
    options?: { successMessage?: string }
  ) => Promise<boolean>;
  onPromptChange: (index: number, patch: Partial<ScreeningPromptItem>) => void;
  onPromptItemsChange: (items: ScreeningPromptItem[]) => void;
  onPromptVideoFile: (index: number, file: File | null) => void;
  onPromptDocumentFile: (index: number, file: File | null) => void;
  onRetryVideoUpload: (index: number) => void;
  onCancelVideoUpload: (index: number) => void;
  onRetryDocumentUpload: (index: number) => void;
  onCancelDocumentUpload: (index: number) => void;
  onPersistPromptDetails?: () => Promise<boolean>;
  onBack: () => void;
  onComplete: () => void;
};

type ProfileTab = 'experience' | 'skills' | 'projects' | 'about';

const PROJECT_ACCENTS = ['#dcf3e6', '#dbeafe', '#fce7f3', '#fef3c7'];
const PROJECT_EMOJIS = ['🗂️', '📱', '🚀', '💡'];

const BOOST_ITEMS: Array<{ icon: typeof Award; label: string; kind: BoostModalKind }> = [
  { icon: Award, label: 'Certifications', kind: 'certifications' },
  { icon: Trophy, label: 'Accomplishments', kind: 'accomplishments' },
  { icon: LinkIcon, label: 'Portfolio links', kind: 'portfolio' },
];

const PROFILE_TABS: Array<{ id: ProfileTab; label: string }> = [
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'about', label: 'About' },
];

function formatYears(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '';
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded} year${rounded === 1 ? '' : 's'} experience`;
}

function formatLocation(draft: OnboardingDraft): string {
  return [draft.location?.city, draft.location?.state, draft.location?.country]
    .filter(Boolean)
    .join(', ');
}

function formatExperienceDates(start?: string, end?: string): string {
  return [start, end].filter(Boolean).join(' – ');
}

function descriptionBullets(description?: string): string[] {
  if (!description?.trim()) return [];
  return description
    .split(/\n|•|·/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function groupSkills(skills: string[] = []) {
  const backend: string[] = [];
  const frontend: string[] = [];
  const devops: string[] = [];
  const other: string[] = [];

  const backendKeys = [
    'python',
    'django',
    'flask',
    'java',
    'node',
    'go',
    'rust',
    'sql',
    'postgres',
    'mysql',
    'api',
    'drf',
    'redis',
    'celery',
  ];
  const frontendKeys = [
    'react',
    'vue',
    'angular',
    'typescript',
    'javascript',
    'css',
    'html',
    'next',
  ];
  const devopsKeys = [
    'docker',
    'kubernetes',
    'aws',
    'azure',
    'gcp',
    'ci',
    'devops',
    'terraform',
    'git',
  ];

  skills.forEach((skill) => {
    const lower = skill.toLowerCase();
    if (backendKeys.some((key) => lower.includes(key))) backend.push(skill);
    else if (frontendKeys.some((key) => lower.includes(key))) frontend.push(skill);
    else if (devopsKeys.some((key) => lower.includes(key))) devops.push(skill);
    else other.push(skill);
  });

  if (other.length) {
    if (!backend.length) backend.push(...other.splice(0, Math.ceil(other.length / 2)));
    if (!frontend.length) frontend.push(...other);
    else devops.push(...other);
  }

  return [
    { label: 'Backend', items: backend },
    { label: 'Frontend', items: frontend },
    { label: 'DevOps / Tools', items: devops },
  ].filter((group) => group.items.length > 0);
}

function StrengthRing({ percent, label }: { percent: number; label: string }) {
  const circumference = 2 * Math.PI * 52;
  const dash = (percent / 100) * circumference;

  return (
    <div className="ssr-strength-ring-wrap">
      <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden>
        <circle cx="60" cy="60" r="52" fill="none" stroke="#eef0f2" strokeWidth="9" />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="#198754"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform="rotate(-90 60 60)"
        />
        <text x="60" y="58" textAnchor="middle" fontSize="24" fontWeight="700" fill="#1f2a24">
          {percent}%
        </text>
        <text x="60" y="76" textAnchor="middle" fontSize="11" fill="#6b7280">
          {label}
        </text>
      </svg>
    </div>
  );
}

function projectAccentClass(index: number): string {
  return `ssr-project-icon ssr-project-icon--${index % PROJECT_ACCENTS.length}`;
}

export default function ReviewStage({
  draft,
  targetRole,
  linkedin,
  resumeUrl,
  sessionUser,
  saving,
  promptItems,
  videoFiles,
  documentFiles,
  videoUrls,
  documentUrls,
  videoUploads,
  documentUploads,
  initials,
  onPersistDraft,
  onPromptChange,
  onPromptItemsChange,
  onPromptVideoFile,
  onPromptDocumentFile,
  onRetryVideoUpload,
  onCancelVideoUpload,
  onRetryDocumentUpload,
  onCancelDocumentUpload,
  onPersistPromptDetails,
  onBack,
  onComplete,
}: ReviewStageProps) {
  const [boostModalKind, setBoostModalKind] = useState<BoostModalKind | null>(null);
  const [experienceModalOpen, setExperienceModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [skillsModalOpen, setSkillsModalOpen] = useState(false);
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('experience');
  const [showFullTimeline, setShowFullTimeline] = useState(false);

  const readiness = useMemo(
    () =>
      resolveOnboardingProfileStrength(
        sessionUser,
        draft,
        targetRole,
        linkedin,
        resumeUrl,
        promptItems,
        videoFiles
      ),
    [sessionUser, draft, targetRole, linkedin, resumeUrl, promptItems, videoFiles]
  );

  const readinessPercent = Math.max(0, Math.min(100, Math.round(readiness.percent)));
  const strengthText = profileStrengthLabel(readinessPercent);

  const checklist = useMemo(() => {
    const hasExperience = Boolean(draft.workExperience?.length);
    const hasSkills = Boolean(draft.skills?.length);
    const hasProjects = Boolean(draft.projects?.length);
    const hasVideos = promptItems.some(
      (item, index) =>
        Boolean((item.fileUrl || '').trim() && item.fileUrl !== 'N/A') ||
        Boolean(videoFiles[index]) ||
        Boolean(videoUrls[index])
    );
    return { hasExperience, hasSkills, hasProjects, hasVideos };
  }, [draft, promptItems, videoFiles, videoUrls]);

  const skillGroups = groupSkills(draft.skills || []);
  const locationLabel = formatLocation(draft);
  const yearsLabel = formatYears(draft.yearsOfExperience);
  const allExperienceItems = draft.workExperience || [];
  const visibleExperienceItems = showFullTimeline
    ? allExperienceItems
    : allExperienceItems.slice(0, 3);
  const projectItems = draft.projects || [];
  const certificationItems = draft.certifications || [];
  const accomplishmentItems = draft.accomplishments || [];

  const username = String(sessionUser?.username || sessionUser?.userName || '').trim();
  const previewHref = username ? buildCandidateProfilePreviewUrl(username) : '';

  const openTabEditor = () => {
    if (activeTab === 'experience') setExperienceModalOpen(true);
    else if (activeTab === 'skills') setSkillsModalOpen(true);
    else if (activeTab === 'projects') setProjectsModalOpen(true);
    else setProfileModalOpen(true);
  };

  return (
    <>
      <header className="ssr-page-header">
        <div className="ssr-page-header-copy">
          <h1 className="ssr-heading">Review your profile</h1>
          <p>
            We&apos;ve built a strong draft from your resume. Polish the details — video answers are
            optional but strongly recommended.
          </p>
        </div>
        {previewHref ? (
          <a className="ssr-preview-btn" href={previewHref} target="_blank" rel="noreferrer">
            <Eye size={16} />
            Preview public profile
          </a>
        ) : null}
      </header>

      <div className="ssr-body">
        <aside className="ssr-left">
          <div className="ssr-card">
            <p className="ssr-strength-title">Profile strength</p>
            <div className="ssr-progress-wrap">
              <StrengthRing percent={readinessPercent} label={strengthText} />
            </div>
            <div className="ssr-checklist">
              <div className="ssr-checklist-item">
                <span className={`ssr-check-icon ${checklist.hasExperience ? 'done' : 'pending'}`}>
                  {checklist.hasExperience ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Experience added
              </div>
              <div className="ssr-checklist-item">
                <span className={`ssr-check-icon ${checklist.hasSkills ? 'done' : 'pending'}`}>
                  {checklist.hasSkills ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Skills detected
              </div>
              <div className="ssr-checklist-item">
                <span className={`ssr-check-icon ${checklist.hasProjects ? 'done' : 'pending'}`}>
                  {checklist.hasProjects ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Projects added
              </div>
              <div className={`ssr-checklist-item ${checklist.hasVideos ? '' : 'pending-text'}`}>
                <span className={`ssr-check-icon ${checklist.hasVideos ? 'done' : 'pending'}`}>
                  {checklist.hasVideos ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Add video answers
              </div>
            </div>
          </div>

          <div className="ssr-boost-card">
            <p className="ssr-boost-title ssr-heading">Make your profile even stronger</p>
            <div className="ssr-boost-list">
              {BOOST_ITEMS.map((item) => {
                const count =
                  item.kind === 'certifications'
                    ? certificationItems.length
                    : item.kind === 'accomplishments'
                      ? accomplishmentItems.length
                      : projectItems.filter((project) => Boolean(project.link)).length;
                return (
                  <button
                    type="button"
                    className="ssr-boost-item"
                    key={item.label}
                    onClick={() => setBoostModalKind(item.kind)}
                    aria-label={`Add ${item.label.toLowerCase()}`}
                  >
                    <item.icon size={18} />
                    <span className="ssr-boost-item-label">{item.label}</span>
                    {count > 0 ? <span className="ssr-boost-item-count">{count}</span> : null}
                    <span className="ssr-boost-item-action">
                      <Plus size={14} aria-hidden />
                      Add
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <main className="ssr-center">
          <div className="ssr-card ssr-profile-card">
            <div className="ssr-avatar-wrap">
              <div className="ssr-avatar">{initials}</div>
              <button
                type="button"
                className="ssr-avatar-edit"
                onClick={() => setProfileModalOpen(true)}
                aria-label="Edit role and summary"
              >
                <Pencil size={12} color="#198754" />
              </button>
            </div>
            <div className="ssr-profile-main">
              <div className="ssr-profile-head">
                <h3 className="ssr-heading">
                  {targetRole || draft.currentJobTitle || 'Your role'}
                </h3>
                <button
                  type="button"
                  className="ssr-edit-link"
                  onClick={() => setProfileModalOpen(true)}
                >
                  Edit role <Pencil size={13} />
                </button>
              </div>

              <p className="ssr-profile-desc">{draft.overview || 'Add a professional summary.'}</p>

              <div className="ssr-meta-row">
                {yearsLabel ? (
                  <span className="ssr-meta-item">
                    <Briefcase size={14} /> {yearsLabel}
                  </span>
                ) : null}
                {locationLabel ? (
                  <span className="ssr-meta-item">
                    <MapPin size={14} /> {locationLabel}
                  </span>
                ) : null}
                <span className="ssr-meta-item">
                  <Globe size={14} /> Open to remote
                </span>
              </div>
            </div>
          </div>

          <div className="ssr-card ssr-tabs-card">
            <div className="ssr-tabs-head">
              <div className="ssr-tabs" role="tablist" aria-label="Profile sections">
                {PROFILE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`ssr-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button type="button" className="ssr-edit-link" onClick={openTabEditor}>
                Edit <Pencil size={13} />
              </button>
            </div>

            <div className="ssr-tab-panel" role="tabpanel">
              {activeTab === 'experience' ? (
                <>
                  {visibleExperienceItems.length > 0 ? (
                    visibleExperienceItems.map((item, index) => {
                      const bullets = descriptionBullets(item.description);
                      return (
                        <div className="ssr-exp-item" key={`${item.company}-${index}`}>
                          <div className="ssr-exp-dot" />
                          <div className="ssr-exp-content">
                            <p className="ssr-exp-role">{item.title || 'Role'}</p>
                            <p className="ssr-exp-meta">
                              <span className="ssr-exp-company">{item.company || 'Company'}</span>
                              {formatExperienceDates(item.startDate, item.endDate) ? (
                                <span>
                                  {' '}
                                  · {formatExperienceDates(item.startDate, item.endDate)}
                                </span>
                              ) : null}
                            </p>
                            {bullets.length > 0 ? (
                              <ul className="ssr-exp-bullets">
                                {bullets.map((bullet) => (
                                  <li key={bullet}>{bullet}</li>
                                ))}
                              </ul>
                            ) : item.description ? (
                              <p className="ssr-exp-plain">{item.description}</p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="ssr-empty-copy">No experience yet. Tap Edit to add roles.</p>
                  )}
                  {allExperienceItems.length > 3 ? (
                    <button
                      type="button"
                      className="ssr-view-timeline-btn"
                      onClick={() => setShowFullTimeline((prev) => !prev)}
                    >
                      {showFullTimeline ? 'Show less' : 'View full timeline'}
                      <ChevronRight size={16} />
                    </button>
                  ) : null}
                </>
              ) : null}

              {activeTab === 'skills' ? (
                skillGroups.length > 0 ? (
                  skillGroups.map((group) => (
                    <div className="ssr-skill-group" key={group.label}>
                      <p className="ssr-skill-group-title">{group.label}</p>
                      <div className="ssr-tag-row">
                        {group.items.map((tag) => (
                          <span className="ssr-tag" key={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="ssr-empty-copy">No skills yet. Tap Edit to add some.</p>
                )
              ) : null}

              {activeTab === 'projects' ? (
                projectItems.length > 0 ? (
                  projectItems.map((project, index) => (
                    <div className="ssr-project-item" key={`${project.name}-${index}`}>
                      <div className={projectAccentClass(index)}>
                        {PROJECT_EMOJIS[index % PROJECT_EMOJIS.length]}
                      </div>
                      <div className="ssr-project-main">
                        <p className="ssr-project-name">{project.name || 'Untitled project'}</p>
                        {project.description ? (
                          <p className="ssr-project-sub">{project.description}</p>
                        ) : null}
                        {project.link ? (
                          <a
                            className="ssr-project-link"
                            href={project.link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {project.link}
                          </a>
                        ) : null}
                      </div>
                      <ChevronRight size={18} color="#9ca3af" />
                    </div>
                  ))
                ) : (
                  <p className="ssr-empty-copy">
                    No projects yet. Add portfolio links from the sidebar.
                  </p>
                )
              ) : null}

              {activeTab === 'about' ? (
                <div className="ssr-about-sections">
                  <div className="ssr-about-block">
                    <div className="ssr-about-head">
                      <p className="ssr-skill-group-title">Certifications</p>
                      <button
                        type="button"
                        className="ssr-edit-link"
                        onClick={() => setBoostModalKind('certifications')}
                      >
                        Add
                      </button>
                    </div>
                    {certificationItems.length > 0 ? (
                      <ul className="ssr-extras-list">
                        {certificationItems.map((item, index) => (
                          <li key={`cert-${item.name}-${index}`}>
                            <strong>{item.name}</strong>
                            {item.date ? <span> · {item.date}</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ssr-empty-copy ssr-empty-copy--compact">
                        No certifications added yet.
                      </p>
                    )}
                  </div>

                  <div className="ssr-about-block">
                    <div className="ssr-about-head">
                      <p className="ssr-skill-group-title">Accomplishments</p>
                      <button
                        type="button"
                        className="ssr-edit-link"
                        onClick={() => setBoostModalKind('accomplishments')}
                      >
                        Add
                      </button>
                    </div>
                    {accomplishmentItems.length > 0 ? (
                      <ul className="ssr-extras-list">
                        {accomplishmentItems.map((item, index) => (
                          <li key={`acc-${item.name}-${index}`}>
                            <strong>{item.name}</strong>
                            {item.date ? <span> · {item.date}</span> : null}
                            {item.description ? (
                              <p className="ssr-extras-desc">{item.description}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ssr-empty-copy ssr-empty-copy--compact">
                        No accomplishments added yet.
                      </p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </main>

        <aside className="ssr-right" id="ssr-screening-prompts">
          <div className="ssr-stand-out-card ssr-stand-out-card--prompts">
            <p className="ssr-stand-out-title">Your #1 profile signal</p>
            <p className="ssr-stand-out-body">
              Screening prompts are the <b>most important</b> part of your SkipScreen profile.
              Recruiters weigh video answers more than resume details — profiles with them get{' '}
              <b>3× more</b> responses.
            </p>
          </div>
          <OnboardingPromptSection
            promptItems={promptItems}
            videoFiles={videoFiles}
            documentFiles={documentFiles}
            videoUrls={videoUrls}
            documentUrls={documentUrls}
            videoUploads={videoUploads}
            documentUploads={documentUploads}
            targetRole={targetRole}
            draft={draft}
            sessionUser={sessionUser}
            variant="sidebar"
            onPromptItemsChange={onPromptItemsChange}
            onPromptChange={onPromptChange}
            onVideoFile={onPromptVideoFile}
            onDocumentFile={onPromptDocumentFile}
            onRetryVideoUpload={onRetryVideoUpload}
            onCancelVideoUpload={onCancelVideoUpload}
            onRetryDocumentUpload={onRetryDocumentUpload}
            onCancelDocumentUpload={onCancelDocumentUpload}
            onPersistPromptDetails={onPersistPromptDetails}
          />
        </aside>
      </div>

      <div className="ssr-footer">
        <button type="button" className="ssr-back-btn" onClick={onBack} disabled={saving}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="ssr-footer-note">
          <ShieldCheck size={16} />
          You can edit anytime before publishing
        </div>
        <div className="ssr-footer-actions">
          <button
            type="button"
            className="ssr-edit-profile-btn"
            onClick={() => setProfileModalOpen(true)}
            disabled={saving}
          >
            Edit profile
          </button>
          <button type="button" className="ssr-publish-btn" onClick={onComplete} disabled={saving}>
            {saving ? 'Saving…' : 'Complete onboarding'} <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <OnboardingBoostModal
        kind={boostModalKind}
        draft={draft}
        saving={saving}
        onHide={() => setBoostModalKind(null)}
        onSave={(patch, successMessage) =>
          onPersistDraft({ draftPatch: patch }, { successMessage })
        }
      />

      <ProfileEditModal
        show={profileModalOpen}
        targetRole={targetRole}
        overview={draft.overview || ''}
        saving={saving}
        onHide={() => setProfileModalOpen(false)}
        onSave={({ targetRole: nextRole, overview }) =>
          onPersistDraft(
            { targetRole: nextRole, draftPatch: { overview } },
            { successMessage: 'Profile updated.' }
          )
        }
      />

      <ExperienceEditModal
        show={experienceModalOpen}
        items={draft.workExperience || []}
        saving={saving}
        onHide={() => setExperienceModalOpen(false)}
        onSave={(items) =>
          onPersistDraft(
            { draftPatch: { workExperience: items } },
            { successMessage: 'Experience updated.' }
          )
        }
      />

      <SkillsEditModal
        show={skillsModalOpen}
        skills={draft.skills || []}
        saving={saving}
        onHide={() => setSkillsModalOpen(false)}
        onSave={(skills) =>
          onPersistDraft({ draftPatch: { skills } }, { successMessage: 'Skills updated.' })
        }
      />

      <ProjectsEditModal
        show={projectsModalOpen}
        items={draft.projects || []}
        saving={saving}
        onHide={() => setProjectsModalOpen(false)}
        onSave={(projects) =>
          onPersistDraft({ draftPatch: { projects } }, { successMessage: 'Projects updated.' })
        }
      />
    </>
  );
}
